/**
 * FormShell - the chrome every full-page form in the app wears.
 *
 * Header (title + description) sitting at the top of a scrollable body, and a
 * `glass-bar` action footer pinned underneath it. Extracted from `BaseForm` so
 * forms that can't use BaseForm's schema-driven field rendering (TemplateForm's
 * split pane, wizards with bespoke bodies) get the same chrome instead of
 * hand-copying it - which is how the copies drifted onto raw `bg-white` /
 * `bg-gray-*` colours in the first place.
 *
 * The shell owns layout and tokens only; the caller owns the body and the
 * footer buttons.
 */
import React from 'react';
import clsx from 'clsx';

export interface FormShellProps {
  /** Root element. `form` when the shell wraps a submitting form. */
  as?: 'form' | 'div';
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  /** Headline shown above the body. Omitted (or `hideHeader`) → no header. */
  title?: React.ReactNode;
  /** Muted line under the title. */
  description?: React.ReactNode;
  hideHeader?: boolean;
  /** Content column width - applied to both the body and the footer row. */
  maxWidth?: string;
  /** Extra classes on the body's padded inner wrapper. */
  bodyClassName?: string;
  /** Footer content (buttons). Falsy → the footer bar is not rendered. */
  footer?: React.ReactNode;
  /** Layout classes for the footer's inner row. */
  footerClassName?: string;
  /** Absolutely-positioned overlay (submission loader) rendered over the shell. */
  overlay?: React.ReactNode;
  /**
   * Sizing for the root. The shell only sets `flex flex-col relative` - the
   * caller decides whether it fills its parent (`h-full`) or flexes
   * (`flex-1 min-h-0`).
   */
  className?: string;
  children: React.ReactNode;
}

const FormShell: React.FC<FormShellProps> = ({
  as = 'div',
  onSubmit,
  title,
  description,
  hideHeader = false,
  maxWidth = 'max-w-3xl',
  bodyClassName,
  footer,
  footerClassName = 'justify-between',
  overlay,
  className,
  children,
}) => {
  const showHeader = !hideHeader && (title || description);
  // `min-h-0` matters when the shell is itself a flex item: a column flex item
  // defaults to `min-height: auto`, which is content-based and quietly beats an
  // explicit `h-full`. Without it the shell grows past its parent, the body
  // never becomes the overflow container, and the footer bar is pushed out of
  // view instead of staying pinned.
  const rootClassName = clsx('flex flex-col relative min-h-0', className);

  const content = (
    <>
      {overlay}

      <div className="flex-1 !overflow-y-auto custom-scrollbar-main min-h-0">
        <div className={clsx('mx-auto w-full', maxWidth)}>
          {showHeader && (
            <div className="px-6 pt-6 pb-4 border-b border-hairline">
              {title && <h2 className="text-lg text-fg-strong mb-1">{title}</h2>}
              {description && <p className="text-sm text-fg-muted">{description}</p>}
            </div>
          )}

          <div className={clsx('px-6 py-6 space-y-6', bodyClassName)}>{children}</div>
        </div>
      </div>

      {footer && (
        <div className="glass-bar border-t border-hairline px-6 py-4 flex-shrink-0">
          <div className={clsx('mx-auto w-full flex gap-2', maxWidth, footerClassName)}>
            {footer}
          </div>
        </div>
      )}
    </>
  );

  if (as === 'form') {
    return (
      <form onSubmit={onSubmit} className={rootClassName}>
        {content}
      </form>
    );
  }

  return <div className={rootClassName}>{content}</div>;
};


export default FormShell;
